/**
 * Shared sanitization for every CP **staff** credits entry path in `apps/ui` (course credits,
 * requirement credits, plan grade min/max, configuration grade alerts / deficiency, items-from-a-list).
 *
 * Invariant: only `.` is allowed as the decimal separator (not `,`), matching validation and tests.
 *
 * Integration checklist (update when adding a credits field):
 * - {@link RequirementCreditsNeonField} → create/edit requirement, requirement detail (`NeonNumberField` + DOM sanitizers)
 * - `items-from-a-list-drawer` → list 1/2 credits ({@link RequirementCreditsNeonField}); criteria credits (`NeonTextField` + `sanitizeCreditsFromSource`)
 * - `add-course-page` → credits `<input type="text">` + edit-mode hydrate from `CourseDetail`
 * - `GradeCreditRangesTable` → min/max cells (used by plan editor + configurations grade alerts)
 * - `grade-credit-table.gradeCreditRowsToStringRecord` → numeric plan rows → table string state
 * - `grade-credit-alerts-panel` / `credit-deficiency-panel` → drawer hydrate + cell updates
 *
 * Read-only UI (grid labels, student course cards, etc.) uses `String(number)` or formatters only — no sanitizer.
 */

export const DEFAULT_CREDITS_INPUT_MAX_DECIMALS = 2;

function stripToNumericDecimalString(value: string): string {
  const cleaned = value.replace(/[^\d.]/g, '');
  const dot = cleaned.indexOf('.');
  if (dot < 0) {
    return cleaned;
  }
  return cleaned.slice(0, dot + 1) + cleaned.slice(dot + 1).replace(/\./g, '');
}

/**
 * Allow only digits and a single decimal point, then keep at most `maxDecimals` digits after the
 * decimal (default {@link DEFAULT_CREDITS_INPUT_MAX_DECIMALS}).
 *
 * @param value - Raw input from a controlled field (may include letters, extra `.`, locale symbols).
 * @param maxDecimals - Max digits after `.`; non-finite or negative values skip fractional clamping.
 * @returns Sanitized string safe to store in controlled input state.
 */
export function sanitizeCreditsInput(
  value: string,
  maxDecimals: number = DEFAULT_CREDITS_INPUT_MAX_DECIMALS
): string {
  const s = stripToNumericDecimalString(value);
  if (!Number.isFinite(maxDecimals) || maxDecimals < 0) {
    return s;
  }
  const dot = s.indexOf('.');
  if (dot < 0) {
    return s;
  }
  if (maxDecimals === 0) {
    return s.slice(0, dot);
  }
  return s.slice(0, dot + 1 + maxDecimals);
}

/**
 * Hydrate a credits text field from API numbers, stored strings, or `modelValue` updates so the
 * same fractional rules apply as while typing (avoids showing full float precision, e.g. `0.523444`).
 */
export function sanitizeCreditsFromSource(
  value: number | string | null | undefined,
  maxDecimals: number = DEFAULT_CREDITS_INPUT_MAX_DECIMALS
): string {
  if (value === null || value === undefined) {
    return '';
  }
  const raw = typeof value === 'number' ? String(value) : value;
  return sanitizeCreditsInput(raw, maxDecimals);
}
